import axios from 'axios';

/** When false, skip POST /api/logging (e.g. login page). Set from _app via setClientLoggingAuthState. */
let clientLoggingHasAuthenticatedUser = false;

/**
 * @param {boolean} hasUser - True when session has a user (aligned with apiHandler session check).
 */
export function setClientLoggingAuthState(hasUser) {
  clientLoggingHasAuthenticatedUser = Boolean(hasUser);
}

const logQueue = [];
let isProcessingQueue = false;
/** While true, intercepted console methods must not enqueue (avoids feedback during queue drain). */
let suppressClientLogForward = false;

const WRAPPER_MARKER = '__hubClientLogWrapper';

/** Native console methods captured before any interception (used for internal diagnostics). */
const nativeConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

/**
 * @param {(...args: unknown[]) => void} fn
 */
function markWrapper(fn) {
  Object.defineProperty(fn, WRAPPER_MARKER, { value: true });
  return fn;
}

/**
 * @param {unknown} fn
 */
function isWrapped(fn) {
  return Boolean(fn && typeof fn === 'function' && fn[WRAPPER_MARKER]);
}

async function processLogQueue() {
  if (isProcessingQueue) return;
  isProcessingQueue = true;
  suppressClientLogForward = true;

  try {
    while (logQueue.length > 0) {
      const logItem = logQueue[0];

      try {
        await axios.post('/api/logging', logItem);
        logQueue.shift();
      } catch {
        logQueue.shift();
        nativeConsole.info('Failed to send log:', logItem);
      }
    }
  } finally {
    suppressClientLogForward = false;
    isProcessingQueue = false;
  }
}

function enqueueLog(payload) {
  logQueue.push(payload);
  void processLogQueue();
}

function asLogContext(raw) {
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }
  if (raw === undefined) {
    return {};
  }
  let serialized;
  try {
    serialized = typeof raw === 'string' ? raw : JSON.stringify(raw);
  } catch {
    serialized = String(raw);
  }
  return { value: serialized };
}

/** Best-effort JSON for telemetry; never throws (circular refs, BigInt, etc.). */
function serializeForLog(value) {
  if (typeof value === 'string') {
    return value;
  }
  try {
    const s = JSON.stringify(value);
    if (s !== undefined) {
      return s;
    }
  } catch {
    // fall through
  }
  return String(value);
}

function serializeRejectionReason(reason) {
  if (reason == null || reason === '') {
    return 'Unknown reason';
  }
  try {
    return JSON.stringify(reason, null, 2);
  } catch {
    return String(reason);
  }
}

const logToServer = (
  level,
  message,
  context = {},
  defaultGlobalSource = '/client/global-logs'
) => {
  if (!clientLoggingHasAuthenticatedUser || suppressClientLogForward) {
    return;
  }

  const ctx = asLogContext(context);
  const source = ctx?.source ? `/client${ctx.source}` : defaultGlobalSource;

  const logPayload = {
    level,
    message: serializeForLog(message),
    context: ctx,
    source,
    timestamp: new Date().toISOString(),
  };

  enqueueLog(logPayload);
};

const interceptConsole = () => {
  if (isWrapped(console.log)) {
    return;
  }

  const defaultGlobalSource = '/client/global-logs';

  console.log = markWrapper((...args) => {
    const [message, ...context] = args;
    logToServer(
      'info',
      message,
      context.length > 0 ? context[0] : {},
      defaultGlobalSource
    );
    nativeConsole.log(...args);
  });

  console.warn = markWrapper((...args) => {
    const [message, ...context] = args;
    logToServer(
      'warn',
      message,
      context.length > 0 ? context[0] : {},
      defaultGlobalSource
    );
    nativeConsole.warn(...args);
  });

  console.error = markWrapper((...args) => {
    const [message, ...context] = args;
    logToServer(
      'error',
      message,
      context.length > 0 ? context[0] : {},
      defaultGlobalSource
    );
    nativeConsole.error(...args);
  });

  window.onerror = (message, source, lineno, colno, error) => {
    logToServer('error', message, {
      source: '/global-errors',
      errorSource: source,
      lineno,
      colno,
      stack: error?.stack,
    });
  };

  window.onunhandledrejection = (event) => {
    logToServer('error', 'Unhandled rejection', {
      reason: serializeRejectionReason(event.reason),
      source: '/unhandled-rejections',
    });
  };
};

export default interceptConsole;
